import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserInfo } from 'src/app/model/User';
import { EventService } from 'src/app/service/event.service';
import { GeneralService } from 'src/app/service/general.service';
import { Modal } from '../controls/modal/modal.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { Subscription, finalize, tap } from 'rxjs';
import { ActivitySettings } from 'src/app/model/Setting';
import { ModalMessageComponent } from '../controls/modal-message/modal-message.component';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ModalConfig } from '../controls/modal/modal-config';

@Component({
    selector: 'app-settings-activity',
    templateUrl: './settings-activity.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-activity.component.css'],
    standalone: false
})
export class SettingsActivityComponent implements OnInit {
  isLoading:boolean = true;
  isSaving:boolean = false;

  private activitySettingsValidators = [
    Validators.required,
    Validators.min(1),
    Validators.max(72),
    Validators.pattern("^[0-9]*$")
  ];

  settings:ActivitySettings;
  prevSetting:ActivitySettings;
  
  settingsForm = new FormGroup({
    enableRetryOnErrorCtrl: new FormControl(),
    enableRetryOnErrorPeriodCtrl: new FormControl<number>(24),
  });
  savingBtn:string = $localize`Save`;
  currentUserAuthInfo : UserInfo;

  constructor(private genService: GeneralService,
    private eventService: EventService,
    private modal: Modal,
    private authService: AuthenticationService,) { }

    get enableRetryOnErrorCtrl() { return this.settingsForm.get('enableRetryOnErrorCtrl'); }
    get enableRetryOnErrorPeriodCtrl() { return this.settingsForm.get('enableRetryOnErrorPeriodCtrl'); }

    ngOnInit(): void {
      this.currentUserAuthInfo = this.authService.getLoggedInUser();
      this.initData();
    }

    initData(showLoading:boolean = true): void{
      if(showLoading){
        this.isLoading = true;
      }
      this.genService.getActivitySettings().pipe(tap(resp => {
        this.settings = resp.body;
        this.prevSetting = Object.assign({}, this.settings);
        this.enableRetryOnErrorCtrl.valueChanges.subscribe(value => {
          this.setRetryOnErrorPeriodValidator(value);
        });
        this.enableRetryOnErrorCtrl.setValue(this.settings.enableRetryOnError);
        this.enableRetryOnErrorPeriodCtrl.setValue(this.settings.enableRetryOnErrorPeriod);
        this.settingsForm.reset(this.settingsForm.value);
      }),
      finalize(() => {
        this.isLoading = false;
      }),
      ).subscribe();
    }

    setRetryOnErrorPeriodValidator(setValidator:boolean): void {
      if(setValidator){
        this.enableRetryOnErrorPeriodCtrl.setValidators(this.activitySettingsValidators);
      }else{
        this.enableRetryOnErrorPeriodCtrl.clearValidators();
      }
  
      this.enableRetryOnErrorPeriodCtrl.updateValueAndValidity();
    }

    save(): void {
      let msg:string = $localize`The changes made on this page will be saved. Do you want to save?`;

      if(this.prevSetting != this.enableRetryOnErrorCtrl.value){
        msg = this.enableRetryOnErrorCtrl.value ? msg + $localize`<br/> <b>Warning</b>: Enabling this setting will allow only new activities with an error to be eligible for retry.` :
                                                  msg + $localize`<br/> <b>Warning</b>: Disabling this setting will prevent existing activities with an error to be eligible for retry.`;
      }

      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Save`,
            message: msg,
            primaryBtn: $localize`Yes`,
            secondaryBtn: $localize`No`,
            width: '450px',
          },
          id: 'otsc-settings-activity-save'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.isSaving = true;
            this.savingBtn = $localize`Saving...`;
            this.settings.enableRetryOnError = this.enableRetryOnErrorCtrl.value;

            if(this.settings.enableRetryOnError){
              this.settings.enableRetryOnErrorPeriod = this.enableRetryOnErrorPeriodCtrl.value;
            }
  
            this.genService.updateActivitySettings(this.settings).pipe(tap(resp => {
              if (resp.body) {
                Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully`);
                this.initData(false);
              } else {
                Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
                this.settingsForm.reset(this.settingsForm.value);
              }
            }),
              finalize(() => {
                this.isSaving = false;
                this.savingBtn = $localize`Save`;
              }),
            ).subscribe();
          }
          sub.unsubscribe();
        });
    }

    reset(): void {
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Reset`,
            message: $localize`The changes made on this page will be lost. Do you want to reset?`,
            primaryBtn: $localize`Yes`,
            secondaryBtn: $localize`No`,
            width: '350px',
          },
          id: 'otsc-settings-activity-reset'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            this.initData();
          }
          sub.unsubscribe();
        });
    }

    canDeactivate() : boolean{
      return !this.settingsForm.dirty;
    }

    getDeactivateModalMessageConfig(): ModalConfig {
      return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-activity-unsaved-changes');
    }
}
